import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Person as PersonIcon,
  Circle as CircleIcon,
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import axios from 'axios';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import config from '../config';

const Contacts = ({ token }) => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [error, setError] = useState('');

  const parseStatus = (status) => {
    if (!status) return 'offline';
    
    if (typeof status === 'string') {
      if (status.includes('UserStatusOnline')) return 'online';
      if (status.includes('UserStatusOffline')) return 'offline';
      if (status.includes('UserStatusRecently')) return 'recently';
      if (status.includes('UserStatusLastWeek')) return 'last_week';
      if (status.includes('UserStatusLastMonth')) return 'last_month';
    }
    return 'offline';
  };

  const fetchContacts = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await axios.get(`${config.API_BASE}/list-contacts`, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('API Response:', response.data);
      
      // Extract contacts from response
      let contactsData = [];
      if (response.data && response.data.contacts) {
        contactsData = response.data.contacts;
      } else if (Array.isArray(response.data)) {
        contactsData = response.data;
      }
      
      console.log('Contacts data:', contactsData);
      
      const formattedContacts = contactsData.map((contact, index) => ({
        id: contact.id || `contact_${index}`,
        contact_id: contact.id || `contact_${index}`,
        first_name: contact.first_name || 'Unknown',
        last_name: contact.last_name || '',
        username: contact.username || 'No username',
        phone: contact.phone || 'No phone',
        status: parseStatus(contact.status),
        is_user: contact.is_user ? 'Yes' : 'No',
        is_mutual_contact: contact.mutual_contact ? 'Yes' : 'No',
        is_bot: contact.is_bot ? 'Yes' : 'No',
        has_photo: contact.photo?.has_photo ? 'Yes' : 'No',
      }));
      
      console.log('Formatted contacts:', formattedContacts);
      setContacts(formattedContacts);
      
      if (formattedContacts.length === 0) {
        toast.info('No contacts found');
      } else {
        toast.success(`Loaded ${formattedContacts.length} contacts`);
      }
      
    } catch (error) {
      console.error('Error fetching contacts:', error);
      
      let errorMessage = 'Failed to fetch contacts';
      if (error.response) {
        if (error.response.status === 401) {
          errorMessage = 'Session expired. Please login again.';
          toast.error(errorMessage);
          // Optionally redirect to login
          // setTimeout(() => window.location.href = '/web/login', 2000);
        } else if (error.response.status === 404) {
          errorMessage = 'Contacts endpoint not found';
        } else {
          errorMessage = `Error ${error.response.status}: ${error.response.data?.detail || 'Unknown error'}`;
        }
      } else if (error.request) {
        errorMessage = 'No response from server. Check if API is running.';
      } else {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchContacts();
    } else {
      setError('No authentication token found. Please login again.');
      setLoading(false);
      toast.error('Please login to view contacts');
    }
  }, [token]);

  const handleExportExcel = () => {
    try {
      if (contacts.length === 0) {
        toast.error('No contacts to export');
        return;
      }

      // Format for export
      const exportData = contacts.map(contact => ({
        'ID': contact.contact_id,
        'First Name': contact.first_name,
        'Last Name': contact.last_name,
        'Username': contact.username,
        'Phone': contact.phone,
        'Status': contact.status,
        'Is User': contact.is_user,
        'Mutual Contact': contact.is_mutual_contact,
        'Is Bot': contact.is_bot,
        'Has Photo': contact.has_photo,
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Contacts');
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(data, `telegram_contacts_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success('Contacts exported successfully!');
    } catch (error) {
      toast.error('Failed to export contacts');
    }
  };

  const filteredContacts = contacts.filter(contact =>
    (contact.first_name && contact.first_name.toLowerCase().includes(search.toLowerCase())) ||
    (contact.last_name && contact.last_name.toLowerCase().includes(search.toLowerCase())) ||
    (contact.username && contact.username.toLowerCase().includes(search.toLowerCase())) ||
    (contact.phone && contact.phone.includes(search))
  );

  const columns = [
    { field: 'contact_id', headerName: 'ID', width: 80 },
    { field: 'first_name', headerName: 'First Name', width: 150 },
    { field: 'last_name', headerName: 'Last Name', width: 150 },
    { field: 'username', headerName: 'Username', width: 150 },
    { field: 'phone', headerName: 'Phone', width: 150 },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Chip
          icon={<CircleIcon sx={{ 
            fontSize: 12,
            color: params.value === 'online' ? '#10b981' : 
                   params.value === 'recently' ? '#f59e0b' : '#64748b'
          }} />}
          label={params.value}
          size="small"
          sx={{
            bgcolor: params.value === 'online' ? 'rgba(16, 185, 129, 0.1)' : 
                    params.value === 'recently' ? 'rgba(245, 158, 11, 0.1)' : 
                    'rgba(100, 116, 139, 0.1)',
            color: params.value === 'online' ? '#10b981' : 
                   params.value === 'recently' ? '#f59e0b' : '#64748b',
            textTransform: 'capitalize',
          }}
        />
      ),
    },
    { field: 'is_user', headerName: 'Is User', width: 100 },
    { field: 'is_mutual_contact', headerName: 'Mutual', width: 100 },
    { field: 'is_bot', headerName: 'Is Bot', width: 100 },
    { 
      field: 'has_photo', 
      headerName: 'Photo', 
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          sx={{
            bgcolor: params.value === 'Yes' ? 'rgba(59, 130, 246, 0.1)' : 'rgba(100, 116, 139, 0.1)',
            color: params.value === 'Yes' ? '#3b82f6' : '#64748b',
          }}
        />
      )
    },
  ];

  return (
    <Box>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Contacts Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchContacts}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Refresh'}
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleExportExcel}
            disabled={contacts.length === 0}
          >
            Export to Excel
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      <Card sx={{ borderRadius: 3, mb: 3, bgcolor: 'background.paper' }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Search contacts..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ maxWidth: 400 }}
            />
            <IconButton>
              <FilterIcon />
            </IconButton>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Loading contacts...</Typography>
            </Box>
          ) : contacts.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <PersonIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="textSecondary">
                No Contacts Found
              </Typography>
              <Typography color="textSecondary" sx={{ mb: 3 }}>
                {token ? 'You have no contacts or Telegram is not connected.' : 'Please login first.'}
              </Typography>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchContacts}
              >
                Try Again
              </Button>
            </Box>
          ) : (
            <>
              <Box sx={{ height: 600 }}>
                <DataGrid
                  rows={filteredContacts}
                  columns={columns}
                  pageSize={10}
                  rowsPerPageOptions={[10, 25, 50]}
                  loading={loading}
                  checkboxSelection
                  onSelectionModelChange={(newSelection) => {
                    setSelectedContacts(newSelection);
                  }}
                  getRowId={(row) => row.id}
                  sx={{
                    border: 'none',
                    '& .MuiDataGrid-cell': {
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      bgcolor: 'rgba(255, 255, 255, 0.05)',
                    },
                  }}
                />
              </Box>

              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography color="textSecondary">
                  Total: {contacts.length} contacts | Showing: {filteredContacts.length}
                </Typography>
                {selectedContacts.length > 0 && (
                  <Chip
                    icon={<PersonIcon />}
                    label={`${selectedContacts.length} selected`}
                    color="primary"
                    variant="outlined"
                  />
                )}
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default Contacts;